import type { KYCQuestionDefinition } from '@anchorage/graphql/generated';
import { handleError } from '@anchorage/sentry';
import type { CustomLinkProps } from '@anchorage/utils';
import { parseSanitize } from '@anchorage/utils';
// oxlint-disable-next-line no-restricted-imports
import { FormFileUploadWithProgress } from '@features/file-upload';
import type { UploadedFile } from '@latitude/file-upload';
import { reactHookForm } from '@latitude/form';
import { useRouter } from 'next/router';
import type { ComponentType, FC } from 'react';
import { useCallback } from 'react';
import { VIEW_ONLY_MODE, kycModeHeaders } from 'utils/kycMode';

const FILE_TYPE = ['pdf', 'gif', 'jpg', 'jpeg', 'png'];

const ACCEPT_FILE_TYPE = FILE_TYPE.map((type) => `.${type}`).join(', ');

type UploadResponseData = {
  Error?: string;
  digest?: string;
};

type Props = {
  question: KYCQuestionDefinition;
  isDisabled?: boolean;
  CustomLink?: ComponentType<CustomLinkProps>;
  isBackoffice?: boolean;
  trackFn?: (questionId: string) => void;
  formID?: string;
};

const UploadComponent: FC<Props> = ({
  question,
  isDisabled = false,
  CustomLink,
  isBackoffice = false,
  trackFn = () => {},
  formID: formIDProp,
}) => {
  const { query } = useRouter();
  const formID = formIDProp ?? (query?.id ? query.id.toString() : '');
  // query values are string | string[] when the param is repeated; like useIsViewOnly
  // in the onboarding app, the first value is authoritative.
  const rawViewOnly = Array.isArray(query?.view_only)
    ? query.view_only[0]
    : query?.view_only;
  const isViewOnly = rawViewOnly === 'true';
  const { control, getValues, setValue } = reactHookForm.useFormContext();

  const fileDigest = getValues(`${question.id}.freeformText`) as string;

  const uploadURL = isBackoffice
    ? `/v1/blob/affiliate/${formID}`
    : `/v1/upload/${formID}`;

  let fileURL;
  if (fileDigest) {
    fileURL = isBackoffice
      ? [`/v1/blobs/${formID}/${question.tag}/${fileDigest}`]
      : [`/v1/uploads/${formID}/${question.tag}/${fileDigest}`];
  }

  const handleUploadComplete = useCallback(
    (
      _file: UploadedFile,
      response: XMLHttpRequest['response'],
      error?: Error,
    ) => {
      if (error) {
        handleError(
          new Error(`Unable to upload the file for ${question.id}`, {
            cause: error,
          }),
        );
      }

      const responseData = response
        ? (JSON.parse(response as string) as UploadResponseData)
        : {};
      const digest = responseData?.digest || '';
      if (digest) {
        setValue(`${question.id}.freeformText`, digest, {
          shouldValidate: true,
        });
        trackFn?.(question.id);
      }
    },
    [question.id, setValue, trackFn],
  );

  const handleOnClear = useCallback(() => {
    setValue(`${question.id}.freeformText`, '', { shouldValidate: true });
    setValue(`${question.id}.file`, null, { shouldValidate: true });
    trackFn?.(question.id);
  }, [question.id, setValue, trackFn]);

  return (
    <FormFileUploadWithProgress
      uploadURL={uploadURL}
      fileURLs={fileURL}
      fileFetchHeaders={isViewOnly ? kycModeHeaders(VIEW_ONLY_MODE) : undefined}
      uploadFileName={question.tag}
      control={control}
      name={`${question.id}.file`}
      disabled={isDisabled}
      message={
        question.footer ? (
          <div className="[&_ul_li]:ml-4 [&_ul_li]:list-disc">
            {parseSanitize(question.footer, CustomLink)}
          </div>
        ) : (
          ''
        )
      }
      onUploadComplete={handleUploadComplete}
      onClear={handleOnClear}
      rawProps={{
        accept: ACCEPT_FILE_TYPE,
        readOnly: question.readOnly || undefined,
      }}
    />
  );
};

export { UploadComponent };
